import React from 'react';
import { useTranslation } from 'react-i18next';

import { ExternalLink, Phone } from 'lucide-react';

import { CopyButton } from './CopyButton';
import { FilePreview } from './FilePreview';
import { MarkdownRenderer } from './MarkdownRenderer';

import type { MetaInfo } from '../types/meta';

interface MessageBubbleProps {
    isUser: boolean;
    isStreaming?: boolean;
    messageId: string;
    content: string | undefined;
    copyButtonContent: string;
    files?: File[];
    meta$$?: MetaInfo[];
    onPhoneClick?: (phone: string) => void;
    onLinkClick?: (url: string) => void;
    onImageClick?: (imageUrl: string) => void;
}

export const MessageBubble = React.memo(
    ({
        isUser,
        isStreaming,
        messageId,
        content,
        copyButtonContent,
        files,
        meta$$,
        onPhoneClick,
        onLinkClick,
        onImageClick,
    }: MessageBubbleProps) => {
        const { t } = useTranslation();
        return (
            <div className="relative break-words rounded-3xl text-sm group/message">
                {/* File previews */}
                {files && files.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                        {files.map((file, index) => (
                            <FilePreview key={index} file={file} />
                        ))}
                    </div>
                )}

                {/* Content */}
                <div>{content && <MarkdownRenderer content={content} isQuery={isUser} messageId={messageId} />}</div>

                {/* Meta$$ UI - only for non-user messages */}
                {!isUser && meta$$ && meta$$.length > 0 && (
                    <div className="mt-4 flex flex-col gap-2">
                        {meta$$.map((meta, index) => (
                            <div key={`${meta.text}-${meta.type}-${index}`}>
                                {meta.type === 'phone' && (
                                    <button
                                        type="button"
                                        onClick={() => onPhoneClick?.(meta.text)}
                                        className="h-10 rounded-[30px] flex items-center gap-2 pl-[13px] pr-[35px] py-2 border-[1.2px] border-solid border-green-500/50 hover:bg-green-50 transition-colors"
                                    >
                                        <Phone className="w-4 h-4 text-green-600" />
                                        <div className="font-medium">{meta.text}</div>
                                    </button>
                                )}

                                {meta.type === 'link' && (
                                    <button
                                        type="button"
                                        onClick={() => onLinkClick?.(meta.text)}
                                        className="h-10 rounded-[30px] flex items-center gap-2 px-[18px] py-2 border-[1.2px] border-solid border-blue-500/50 hover:bg-blue-50 transition-colors"
                                    >
                                        <ExternalLink className="w-4 h-4 text-blue-600" />
                                        <div className="font-medium text-blue-600 break-all">{meta.text}</div>
                                    </button>
                                )}

                                {meta.type === 'image' && (
                                    <button
                                        type="button"
                                        onClick={() => onImageClick?.(meta.text)}
                                        className="block w-full max-w-[200px] rounded-lg overflow-hidden border border-gray-200 p-0 bg-transparent hover:border-gray-300 transition-colors"
                                    >
                                        <img
                                            src={meta.text}
                                            alt={t('agents.message.attachedImage', '첨부 이미지')}
                                            className="w-full h-auto object-contain"
                                            loading="lazy"
                                            onError={e => {
                                                // Hide broken images
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Copy button for assistant messages */}
                {!isUser && content && (
                    <div className="absolute -bottom-4 right-2 flex space-x-1 rounded-lg border bg-background p-1 text-foreground opacity-0 transition-opacity group-hover/message:opacity-100">
                        <CopyButton content={copyButtonContent} />
                    </div>
                )}
            </div>
        );
    }
);

MessageBubble.displayName = 'MessageBubble';
